/* oxlint-disable max-lines-per-function */
import { useCallback, useState } from 'react';
import type React from 'react';

import { Translate, TranslatePlural, useI18n } from '@change-corgi/core/react/i18n';
import { Icon } from '@change-corgi/design-system-legacy/components/icon';
import { iconChevronRight } from '@change-corgi/design-system-legacy/icons';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { buttonResetStyles } from '@change-corgi/design-system-legacy/reset';
import { Heading, Text } from '@change-corgi/design-system-legacy/typography';

import { usePetitionDetailsPageUserData } from 'src/app/pages/petitionDetails/pageContext';
import { useCampaignTracking } from 'src/app/shared/hooks/campaignTracking';

import { useIsSunriseEnabled } from '../../../hooks/isSunriseEnabled';
import { usePetitionDetails } from '../../../hooks/petitionDetails';
import { usePetitionSignatureCount } from '../../../hooks/petitionStats';

import { SignatureThermometer } from './SignatureThermometer';
import { VerifiedSignersIcon } from './VerifiedSignersIcon';

function useSignatureCountHeadingLabel(displayed: number, displayedLocalized: string): string {
	const { translatePlural } = useI18n();
	return translatePlural(
		'corgi.petition_gamma.details.verified_signers_text.signature_count_heading',
		displayed,
		{ countLocalized: displayedLocalized },
	);
}

export const SignatureStatsActiveContainer: React.FC = () => {
	const { signatureCount, signatureGoal } = usePetitionSignatureCount();
	const [clicked, setClicked] = useState<boolean>();
	const { track } = useCampaignTracking();
	const { id } = usePetitionDetails();

	const openVerifiedSignaturesText = useCallback(() => {
		setClicked(!clicked);
		const trackingData = {
			petition_id: id,
			opened: !clicked,
		};
		track('campaign_action_show_verified_signers_text_click', trackingData);
	}, [id, clicked, track]);

	const {
		data: { signatureThermometerEnabled },
	} = usePetitionDetailsPageUserData();

	const {
		data: { isSunriseEnabled },
	} = useIsSunriseEnabled();

	const signatureThermometerConditionalRenderingEnabled =
		signatureThermometerEnabled && signatureCount.displayed < 1000;

	const signatureCountHeadingLabel = useSignatureCountHeadingLabel(
		signatureCount.displayed,
		signatureCount.displayedLocalized,
	);

	return (
		<Box>
			<Box sx={isSunriseEnabled && signatureThermometerEnabled ? { marginBottom: 28 } : {}}>
				<Flex
					mb={8}
					sx={{
						justifyContent: 'center',
						flexDirection: 'column',
						alignItems: 'center',
						gap: 8,
					}}
				>
					<Box>
						<Flex sx={{ flexDirection: 'row', gap: 4 }}>
							<Heading
								as="h2"
								aria-label={signatureCountHeadingLabel}
								sx={{ fontSize: isSunriseEnabled ? 40 : 48 }}
								tabIndex={0}
							>
								{signatureCount.displayedLocalized}
							</Heading>
							<Box>
								<VerifiedSignersIcon
									width="1em"
									height="1em"
									sx={{ color: isSunriseEnabled ? 'sunrise.semantic.icon.accent.primary' : '#006BA9' }}
								/>
							</Box>
						</Flex>
					</Box>
					<button
						type="button"
						onClick={openVerifiedSignaturesText}
						sx={{
							...buttonResetStyles,
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							gap: 4,
							'&:hover': {
								cursor: 'pointer',
							},
						}}
					>
						<Text size={isSunriseEnabled ? 'small' : 'default'}>
							<TranslatePlural
								value="corgi.petition_gamma.details.verified_signers_text.title"
								count={signatureCount.displayed}
							/>
						</Text>
						<Icon
							size={24}
							icon={iconChevronRight}
							data-testid="verified-signatures-toggle-text"
							sx={{
								border: 'none',
								transform: clicked ? 'rotate(-90deg)' : 'rotate(90deg)',
							}}
						></Icon>
					</button>
					{clicked && (
						<Text
							as="div"
							size="default"
							sx={{ textAlign: 'center', fontStyle: 'italic', color: 'neutral-grey600' }}
							data-testid="verified-signatures-text"
						>
							<Translate value="corgi.petition_gamma.details.verified_signers_text.body" />
						</Text>
					)}
				</Flex>
			</Box>
			{signatureThermometerConditionalRenderingEnabled && (
				<SignatureThermometer signatureCount={signatureCount.displayed} nextMilestone={signatureGoal.displayed} />
			)}
		</Box>
	);
};
